import React, { useRef } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { HiOutlineDocumentMagnifyingGlass } from "react-icons/hi2";

import { InfinitePaginatedPromise } from "@/types/InfiniteQuery.type";
import useInfiniteScroll from "@/hooks/useInfiniteScroll";
import Spinner from "@/components/atoms/Spinner";

export type InfiniteScrollListProps<T> = {
  /** Array of items to render */
  items?: T[];
  /** Function to render each item */
  renderItem: (item: T, index: number) => React.ReactNode;
  /** Additional container class names */
  containerClassName?: string;
  /** Flag for initial loading state */
  isLoading?: boolean;
  /** Flag for loading additional items */
  isLoadingMore?: boolean;
  /** Custom loading component */
  renderLoading?: React.ReactNode;
  /** Content to show if no items are available */
  emptyNode?: React.ReactNode;
  /**
   * Callback to load more items when scrolling reaches the bottom.
   * This is passed to your useInfiniteScroll hook.
   */
  loadMore?: () => InfinitePaginatedPromise<T>;
  /** Flag indicating if more items are available */
  hasMore?: boolean;
};

const InfiniteScrollList = <T,>({
  items,
  renderItem,
  containerClassName,
  isLoading,
  isLoadingMore,
  renderLoading,
  emptyNode,
  loadMore,
  hasMore = false,
}: InfiniteScrollListProps<T>) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const observerRef = useInfiniteScroll({
    containerRef,
    hasNextPage: hasMore,
    fetchNextPage: loadMore,
  });

  return (
    <div ref={containerRef} className={clsx("max-w-full overflow-auto text-sm focus:outline-none", containerClassName)}>
      {isLoading || items === undefined
        ? renderLoading || <IsLoading />
        : items.length > 0
          ? items.map((item, index) => <React.Fragment key={index}>{renderItem(item, index)}</React.Fragment>)
          : emptyNode || <NoResults />}

      {isLoadingMore && (renderLoading || <IsLoading />)}

      <div ref={observerRef} />
    </div>
  );
};

export default InfiniteScrollList;

const NoResults = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-center justify-center px-3 py-4 text-center text-sm text-quaternary">
      <HiOutlineDocumentMagnifyingGlass className="mb-2 size-8" />
      <p>{t("noResults.found")}</p>
      <p>{t("noResults.try")}</p>
    </div>
  );
};

const IsLoading = () => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 px-3 py-4 text-sm text-quaternary">
      <Spinner />
      <p>{t("searching")}</p>
    </div>
  );
};
